import moment from "moment-timezone";
import gql from "graphql-tag";
import { sumBy, cloneDeep } from "lodash";

import { mathFix } from "../mathServices";
import { getEmptyDocument } from "../documentServices";
import { getAccountingClosingDate } from "../accountingPeriods";
import { getDBFormattedDate, getDBFormattedEndMonthDate } from "../dateServices";
import { computeDebitCredit } from "../../../../backend/src/common/accountingLine/accountingLineServices";
import { decliningBalanceFactor } from "../../../../backend/src/common/audit/auditFunctions";

import CREATE_RECONCILIATION from "../../graphql/reconciliation/CreateReconciliation.gql";
import DELETE_RECONCILIATION from "../../graphql/reconciliation/DeleteReconciliation.gql";

// ++++++++ COMMONS +++++++++++++++++++++++
// ++++ The DatePicker needs a format 'YYYY-MM-DD' +++++
export function formatDatePicker(date: string) {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    return formattedDate;
}

export function formatDate(date: string) {
    if (!date) return "";
    return moment(date).startOf("day").format("DD/MM/YYYY");
}

export function closedPeriod(accountingPeriod: any) {
    if (accountingPeriod?.closed) {
        return true;
    }
    return false;
}

// ++++++++++ FIXEDASSETS +++++++++++++++++++++
// export const vatList = [
//     { value: "20", text: '20' },
//     { value: "10", text: '10' },
//     { value: "08", text: '8.5' },
//     { value: "05", text: '5.5' },
//     { value: "02", text: '2.1' },
// ]

async function errorOnRegul68LGap(
    currentAlAcrData: any,
    linear68AccountingLines: Array<any>,
    firstMonthAmount: number = 0,
    linearMonthAmountSigned: number,
    aLObject6811: any,
) {
    let regul68Gap = 0;
    let importedCurrentMonthAmount = 0;

    // ** not fully depreciated
    if (
        currentAlAcrData.previousDepreciationAmountSigned &&
        currentAlAcrData.previousDepreciationAmountSigned < currentAlAcrData.amountSigned
    ) {
        if (linear68AccountingLines.length || firstMonthAmount) {
            const sumFiltered68: number = linear68AccountingLines?.length
                ? mathFix(sumBy(linear68AccountingLines, "amountSigned"))
                : 0; // ** previous months depreciations

            //** calculate current month depreciation
            // ** or get errors on N-1 last month
            if (sumFiltered68 || firstMonthAmount) {
                importedCurrentMonthAmount = mathFix(
                    currentAlAcrData.previousDepreciationAmountSigned +
                        firstMonthAmount -
                        sumFiltered68,
                    2,
                );

                const calculatedMonthDepreciation =
                    firstMonthAmount || linearMonthAmountSigned || 0;

                regul68Gap = mathFix(importedCurrentMonthAmount - calculatedMonthDepreciation, 2);
                const oneWeekAmount = mathFix(linearMonthAmountSigned / 4, 2);
                if (regul68Gap && Math.abs(regul68Gap) > oneWeekAmount) {
                    const regul68GapAbsolute = Math.abs(regul68Gap);
                    const gapSign = regul68Gap > 0 ? -1 : 1;
                    const monthGap = Math.floor(Math.abs(regul68Gap) / aLObject6811.amountSigned);
                    const monthGapSigned = monthGap * gapSign;
                    const fullMonthsDepreciation = aLObject6811.amountSigned * monthGap;
                    const remainingDepreciation = regul68GapAbsolute - fullMonthsDepreciation;
                    const daysGap = Math.floor(
                        (30 * remainingDepreciation) / aLObject6811.amountSigned,
                    );
                    const daysGapSigned = daysGap * gapSign;
                    const suggestedDate = moment(currentAlAcrData.linearSpreadStartDate)
                        .add(monthGapSigned, "months")
                        .add(daysGapSigned, "days")
                        .format("YYYY-MM-DD");
                    return {
                        documents: [],
                        errorOnRegul68LGap: {
                            calculatedSumAmount: mathFix(
                                sumFiltered68 + calculatedMonthDepreciation,
                                2,
                            ),
                            regul68Gap: regul68Gap,
                            suggestedDate: suggestedDate,
                            currentAlAcrData: currentAlAcrData,
                        },
                    };
                }
                return importedCurrentMonthAmount;
            }
        }
    }
    return linearMonthAmountSigned;
}

async function errorOnRegul68DerogationGap(
    currentAlAcrData: any,
    derogationDepreciationTable: Array<any>,
    derogation6878AccountingLines: Array<any>,
) {
    let regulGap = 0;
    let importedCurrentMonthAmount = 0;

    // ** not fully depreciated
    const previousDepreciationAmountSigned_LD_sum =
        mathFix(
            currentAlAcrData.previousDepreciationAmountSigned +
                currentAlAcrData.previousDerogationDepreciationAmountSigned,
            2,
        ) || 0;
    if (previousDepreciationAmountSigned_LD_sum < currentAlAcrData.amountSigned) {
        if (derogation6878AccountingLines.length) {
            const sumDerogationAccountingLines: number =
                mathFix(sumBy(derogation6878AccountingLines, "amountSigned"), 2) || 0; // ** previous months depreciations

            //** calculate current month depreciation
            // ** or get errors on N-1 last month
            if (sumDerogationAccountingLines) {
                importedCurrentMonthAmount = mathFix(
                    currentAlAcrData.previousDerogationDepreciationAmountSigned -
                        sumDerogationAccountingLines,
                    2,
                );
                // ** always last table line: send if date === lastClosedPeriodEnd
                const currentYearTableLine: any =
                    derogationDepreciationTable?.[derogationDepreciationTable.length - 1] || {};
                const calculatedMonthDepreciation =
                    currentYearTableLine?.derogationCurrentMonthAmountSigned || 0;

                regulGap = mathFix(importedCurrentMonthAmount - calculatedMonthDepreciation, 2);
                const oneWeekAmount = mathFix(calculatedMonthDepreciation / 4, 2);
                if (regulGap && Math.abs(regulGap) > oneWeekAmount) {
                    const regul68GapAbsolute = Math.abs(regulGap);
                    const gapSign = regulGap > 0 ? -1 : 1;
                    const monthGap = Math.abs(regulGap);
                    const monthGapSigned = monthGap * gapSign;
                    const daysGap = Math.floor((30 * monthGapSigned) / calculatedMonthDepreciation);
                    const daysGapSigned = daysGap * gapSign;
                    let suggestedDate = "";
                    if (daysGap <= 7) {
                        // ** non-significant gap
                    } else {
                        // // jours restant en avant et en arrière
                        if (daysGap <= currentYearTableLine?.derogationPeriodMonthsN * 30) {
                            // ** gap in current year
                            suggestedDate = moment(currentAlAcrData.date)
                                .add(Math.round(daysGap / 30), "months")
                                .format("YYYY-MM-DD");
                            return {
                                documents: [],
                                errorOnRegul68LGap: {
                                    calculatedSumAmount: mathFix(
                                        sumDerogationAccountingLines + calculatedMonthDepreciation,
                                        2,
                                    ),
                                    regul68Gap: regulGap,
                                    suggestedDate: suggestedDate,
                                    currentAlAcrData: currentAlAcrData,
                                },
                            };
                        } else {
                            // ** gap on more years
                            for (let ind = -1; ind > -derogationDepreciationTable.length; ind--) {
                                const currentYearTableLine: any =
                                    derogationDepreciationTable?.[ind] || {};
                                const calculatedMonthDepreciation =
                                    currentYearTableLine?.derogationCurrentMonthAmountSigned || 0;
                                regulGap = mathFix(
                                    importedCurrentMonthAmount - calculatedMonthDepreciation,
                                    2,
                                );
                                if (regulGap && Math.abs(regulGap) > oneWeekAmount) {
                                    const regul68GapAbsolute = Math.abs(regulGap);
                                    const gapSign = regulGap > 0 ? -1 : 1;
                                    const monthGap = Math.abs(regulGap);
                                    const monthGapSigned = monthGap * gapSign;
                                    const daysGap = Math.floor(
                                        (30 * monthGapSigned) / calculatedMonthDepreciation,
                                    );
                                    const daysGapSigned = daysGap * gapSign;
                                    suggestedDate = moment(currentAlAcrData.date)
                                        .add(monthGapSigned, "months")
                                        .add(daysGapSigned, "days")
                                        .format("YYYY-MM-DD");
                                    return {
                                        documents: [],
                                        errorOnRegul68LGap: {
                                            calculatedSumAmount: mathFix(
                                                sumDerogationAccountingLines +
                                                    calculatedMonthDepreciation,
                                                2,
                                            ),
                                            regul68Gap: regulGap,
                                            suggestedDate: suggestedDate,
                                            currentAlAcrData: currentAlAcrData,
                                        },
                                    };
                                }
                            }

                            return {
                                documents: [],
                                errorOnRegul6878DerogGap: {
                                    calculatedSumAmount: mathFix(
                                        sumDerogationAccountingLines + calculatedMonthDepreciation,
                                        2,
                                    ),
                                    regul68Gap: regulGap,
                                    suggestedDate: suggestedDate,
                                    currentAlAcrData: currentAlAcrData,
                                },
                            };
                        }
                    }
                }
                return importedCurrentMonthAmount;
            }
        }
    }
    return regulGap;
}

// ******************* GENERATE SPREAD ****************
export async function generateLocalSpread(
    apollo,
    store,
    currentData: any,
    lastClosedAndFirstOpenAccountingPeriods: any,
    currentUserId: string,
    fromCutPrevious: boolean = false,
    company: any,
    accountingPeriod: any,
    lastSituationDateYYYYMMDD: string = undefined,
    // companyFirstPeriod: any
) {
    // ** = this.currentAlAcrData
    // ** look for rate and mode
    // ++ generate all accountingLines
    // ++ then depreciationTable
    // ++ then filter als only in not closed periods and generate monthly documents

    // .. used in checkFixedAssetCutPrevious : true
    const lastClosedAndFirstOpen = lastClosedAndFirstOpenAccountingPeriods;

    const lastClosedPeriodEnd = lastClosedAndFirstOpen?.lastClosedPeriod?.end
        ? moment(lastClosedAndFirstOpen.lastClosedPeriod.end).format("YYYY-MM-DD")
        : undefined;

    const accountingPeriodEnd = accountingPeriod?.end
        ? moment(accountingPeriod.end).format("YYYY-MM-DD")
        : undefined;

    let currentAlAcrData = JSON.parse(JSON.stringify(currentData));

    if (currentAlAcrData?.type === "N/A") {
        return {
            currentAlAcrData: currentAlAcrData,
            documents: [],
        };
    } else {
        // ******* ALL TYPES OF DEPRECATIONS ****
        // const data = []
        const linear68AccountingLines = [];
        const linear28AccountingLines = [];
        const decliningAccountingLines = [];
        const derogation6878AccountingLines = [];
        const derogation145AccountingLines = [];
        let derogationAccountingLines = [];

        let document: any = {};

        // ++++++++++ accountingLines structure ++++++

        if (!fromCutPrevious) {
            //     currentAlAcrData.linearDepreciation = this.depreciationType === "Linéaire" || false
            // } else {
            // ** delete previous pre-wording
            if (currentAlAcrData.fixedAssetWording.includes("Dot. Amos - D - ")) {
                currentAlAcrData.fixedAssetWording = currentAlAcrData.fixedAssetWording.replace(
                    "Dot. Amos - D - ",
                    "",
                );
            } else if (currentAlAcrData.fixedAssetWording.includes("Dot. Amos - L - ")) {
                currentAlAcrData.fixedAssetWording = currentAlAcrData.fixedAssetWording.replace(
                    "Dot. Amos - L - ",
                    "",
                );
            } else if (currentAlAcrData.fixedAssetWording.includes("Dot. Amos - ")) {
                currentAlAcrData.fixedAssetWording = currentAlAcrData.fixedAssetWording.replace(
                    "Dot. Amos - ",
                    "",
                );
            }
        }

        // +++++++++++++++++++++++++++++++++++++++++++++
        // ++++++++ LINEAR DEPRECATION: always necessary

        let subDate: any = {};
        let accAcc68;
        if (currentAlAcrData.accountableAccount?.name.startsWith("20")) {
            accAcc68 = await getAuditConnectAccountableAccount(store, apollo, "681110");
        } else {
            accAcc68 = await getAuditConnectAccountableAccount(store, apollo, "681120");
        }

        const aLObject6811 = {
            date: "",
            wording: currentAlAcrData.dotAmoType + currentAlAcrData.fixedAssetWording,
            amountSigned: 0,
            amount: 0,
            accountableAccount: accAcc68,
            type: "MANAGACCOU",
            company: { connect: { id: company.id } },
        };

        const aa28 = currentAlAcrData.accountableAccountDepreciation?.name
            ? await getAuditConnectAccountableAccount(
                  store,
                  apollo,
                  currentAlAcrData.accountableAccountDepreciation.name,
              )
            : currentAlAcrData.accountableAccount?.name
              ? await getAuditConnectAccountableAccount(
                    store,
                    apollo,
                    currentAlAcrData.accountableAccount.name.slice(0, 1) +
                        8 +
                        currentAlAcrData.accountableAccount.name.slice(1, 5),
                    undefined,
                    undefined,
                    true,
                )
              : undefined;

        const aLObject28 = {
            date: "",
            wording: currentAlAcrData.dotAmoType + currentAlAcrData.fixedAssetWording,
            amountSigned: 0,
            amount: 0,
            accountableAccount: aa28,
            type: "MANAGACCOU",
            company: { connect: { id: company.id } },
        };

        currentAlAcrData.linearSpreadStartDate = currentAlAcrData.linearSpreadStartDate
            ? moment(currentAlAcrData.linearSpreadStartDate).startOf("day").format("YYYY-MM-DD")
            : moment(currentAlAcrData.date).startOf("day").format("YYYY-MM-DD");

        currentAlAcrData.linearSpreadMonthCount = currentAlAcrData.selectedYearDuration * 12;
        currentAlAcrData.linearSpreadEndDate = moment(currentAlAcrData.linearSpreadStartDate)
            .clone()
            .add(currentAlAcrData.selectedYearDuration, "years")
            .subtract(1, "days")
            .startOf("day")
            .format("YYYY-MM-DD");

        let dayOfFirstMonth: number = null;
        let endOfFirstMonthNb: number = null;

        subDate = currentAlAcrData.linearSpreadStartDate;
        // ** if in first period
        if (
            currentAlAcrData.linearSpreadStartDate <=
                moment(currentAlAcrData.companyFirstPeriod.end).format("YYYY-MM-DD") &&
            currentAlAcrData.linearSpreadStartDate >=
                moment(currentAlAcrData.companyFirstPeriod.start).format("YYYY-MM-DD")
        ) {
            currentAlAcrData.alFirstPeriod = {
                // id: "",
                start:
                    currentAlAcrData.companyFirstPeriod.start?.length === 10
                        ? currentAlAcrData.companyFirstPeriod.start
                        : moment(currentAlAcrData.companyFirstPeriod.start).format("YYYY-MM-DD"),
                end:
                    currentAlAcrData.companyFirstPeriod.end.length === 10
                        ? currentAlAcrData.companyFirstPeriod.end
                        : moment(currentAlAcrData.companyFirstPeriod.end).format("YYYY-MM-DD"),
                // closed: true
            };
        } else {
            // ** if not in first period,
            // ** if periodEnd MMDD is after linearSpreadStartDate MMDD
            // ** (ex : 02-11 < 31-12 return true  |  02-11 > 01-01 return false)
            // ** same year
            // ** else next year
            const periodEndYYYY = parseInt(
                moment(currentAlAcrData.linearSpreadStartDate).format("YYYY"),
            );

            const periodEndMM = parseInt(moment(accountingPeriodEnd).format("MM")) - 1; // ** moment doc : Accepts numbers from 0 to 11. If the range is exceeded, it will bubble up to the year.
            const periodEndDD = parseInt(moment(accountingPeriodEnd).format("DD"));

            let purchaseFirstPeriodEnd = moment(
                currentAlAcrData.linearSpreadStartDate,
                "YYYY-MM-DD",
            )
                .clone()
                .year(periodEndYYYY)
                .month(periodEndMM)
                .date(periodEndDD)
                .format("YYYY-MM-DD");

            // ** if purchaseFirstPeriodEnd is before linearSpreadStartDate
            // ** add 1 year
            if (
                purchaseFirstPeriodEnd <
                moment(currentAlAcrData.linearSpreadStartDate).format("YYYY-MM-DD")
            ) {
                purchaseFirstPeriodEnd = moment(
                    currentAlAcrData.linearSpreadStartDate,
                    "YYYY-MM-DD",
                )
                    .clone()
                    .year(periodEndYYYY + 1)
                    .month(periodEndMM)
                    .date(periodEndDD)
                    .format("YYYY-MM-DD");
            }

            let purchaseFirstPeriodStart: any = {};

            purchaseFirstPeriodStart = moment(purchaseFirstPeriodEnd)
                .add(-1, "years")
                .add(1, "days")
                .startOf("day")
                .format("YYYY-MM-DD");

            currentAlAcrData.alFirstPeriod = {
                // id: "",
                start: purchaseFirstPeriodStart,
                end: purchaseFirstPeriodEnd,
                // closed: true
            };
        }

        // currentAlAcrData.firstPeriod = this.inAccountingPeriod

        // currentAlAcrData.firstPeriodStart = getDBFormattedDate(this.inAccountingPeriod.start)

        // currentAlAcrData.nbDaysFirstYearPeriod = moment(getDBFormattedDate(this.inAccountingPeriod.end)).diff(this.inAccountingPeriod.start, 'days') +1

        currentAlAcrData.nbDaysFirstYearPeriod =
            moment(currentAlAcrData.alFirstPeriod.end).diff(
                currentAlAcrData.alFirstPeriod.start,
                "days",
            ) + 1;

        dayOfFirstMonth = parseInt(
            moment(currentAlAcrData.linearSpreadStartDate) // used
                .format("DD"),
            10,
        );

        endOfFirstMonthNb = moment(currentAlAcrData.linearSpreadStartDate) // used
            .endOf("month")
            .date();

        currentAlAcrData.nbDaysLastYearPeriod = moment(
            currentAlAcrData.linearSpreadEndDate,
        ).dayOfYear();

        const depreciationBasis =
            currentAlAcrData.depreciableAmountSigned || currentAlAcrData.amountSigned;

        const linearMonthAmountSigned = mathFix(
            depreciationBasis / currentAlAcrData.linearSpreadMonthCount,
            2,
        );

        // ++++++++++ FIRST LINEAR PARTIAL MONTH ++++++++++++
        const endOfFirstMonthDate = getDBFormattedDate(moment(subDate).endOf("month"));

        const firstMonthAmount = mathFix(
            (linearMonthAmountSigned * (endOfFirstMonthNb - dayOfFirstMonth + 1)) /
                endOfFirstMonthNb,
            2,
        );

        let gapFirstMonthRegulLinearMonthAmountSigned = firstMonthAmount;
        if (moment(endOfFirstMonthDate).format("YYYY-MM-DD") == lastClosedPeriodEnd) {
            // eslint-disable-next-line no-await-in-loop
            const testErrors: any = await errorOnRegul68LGap(
                currentAlAcrData,
                [],
                firstMonthAmount,
                linearMonthAmountSigned,
                aLObject6811,
            );

            if (testErrors) {
                if (testErrors?.errorOnRegul68LGap?.suggestedDate) {
                    return testErrors;
                } else {
                    gapFirstMonthRegulLinearMonthAmountSigned = testErrors;
                }
            }
        }

        aLObject6811.date = endOfFirstMonthDate;
        aLObject6811.amountSigned = gapFirstMonthRegulLinearMonthAmountSigned;
        aLObject6811.amount = Math.abs(gapFirstMonthRegulLinearMonthAmountSigned);
        linear68AccountingLines.push({ ...aLObject6811 });

        aLObject28.date = endOfFirstMonthDate;
        aLObject28.amountSigned = -gapFirstMonthRegulLinearMonthAmountSigned;
        aLObject28.amount = Math.abs(gapFirstMonthRegulLinearMonthAmountSigned);
        linear28AccountingLines.push({ ...aLObject28 });

        // ++++++++++ MIDDLE LINEAR FULL MONTHS ++++++++++++
        // ** if linearSpreadStartDate != 01MMYYYY : add 1 partial month in for loop
        // ** ex.: 5 years = 60 months - if all full months
        // **                            else : 1 partial month + 59 full + 1 partial = 61 months
        const startFullMonth =
            parseInt(moment(currentAlAcrData.linearSpreadStartDate).format("DD"), 10) === 1;
        const realMiddleFullMonths = startFullMonth
            ? currentAlAcrData.linearSpreadMonthCount - 1
            : currentAlAcrData.linearSpreadMonthCount;

        let newDate = endOfFirstMonthDate;

        for (let a = 1; a < realMiddleFullMonths; a++) {
            newDate = getDBFormattedDate(
                moment(newDate).add(1, "days").endOf("month").startOf("day").format(),
            );

            let gapRegulLinearMonthAmountSigned = linearMonthAmountSigned;
            if (moment(newDate).format("YYYY-MM-DD") == lastClosedPeriodEnd) {
                // eslint-disable-next-line no-await-in-loop
                const testErrors: any = await errorOnRegul68LGap(
                    currentAlAcrData,
                    linear68AccountingLines,
                    undefined,
                    linearMonthAmountSigned,
                    aLObject6811,
                );

                if (testErrors) {
                    if (testErrors?.errorOnRegul68LGap?.suggestedDate) {
                        return testErrors;
                    } else {
                        gapRegulLinearMonthAmountSigned = testErrors;
                    }
                }
            }

            aLObject6811.date = newDate;
            aLObject6811.amountSigned = gapRegulLinearMonthAmountSigned;
            aLObject6811.amount = Math.abs(aLObject6811.amountSigned);
            linear68AccountingLines.push({ ...aLObject6811 });

            aLObject28.date = newDate;
            aLObject28.amountSigned = -gapRegulLinearMonthAmountSigned;
            aLObject28.amount = Math.abs(aLObject28.amountSigned);
            linear28AccountingLines.push({ ...aLObject28 });
        }

        // ++++++++++ LAST LINEAR PARTIAL MONTH ++++++++++++
        let previousMonthsDepreciation = 0;

        if (linear28AccountingLines?.length) {
            previousMonthsDepreciation = mathFix(sumBy(linear28AccountingLines, "amountSigned"), 2);
            let lastLinearDepreciationAmount = 0;

            // ** full depreciated
            if (
                currentAlAcrData.linearSpreadEndDate <
                moment(accountingPeriod?.start).format("YYYY-MM-DD")
            ) {
                lastLinearDepreciationAmount = mathFix(
                    currentAlAcrData.previousDepreciationAmountSigned + previousMonthsDepreciation,
                    2,
                );
            } else {
                // ** not fully depreciated
                lastLinearDepreciationAmount = mathFix(
                    depreciationBasis + previousMonthsDepreciation,
                    2,
                );
            }

            // ** last linear date is the day before start date
            // ** and may be different of endOfMonth
            aLObject6811.date = getDBFormattedDate(currentAlAcrData.linearSpreadEndDate);
            aLObject6811.amountSigned = lastLinearDepreciationAmount;
            aLObject6811.amount = Math.abs(lastLinearDepreciationAmount);
            linear68AccountingLines.push({ ...aLObject6811 });

            aLObject28.date = getDBFormattedDate(currentAlAcrData.linearSpreadEndDate);
            aLObject28.amountSigned = -lastLinearDepreciationAmount;
            aLObject28.amount = Math.abs(lastLinearDepreciationAmount);
            linear28AccountingLines.push({ ...aLObject28 });

            // if (
            //     currentAlAcrData.linearSpreadEndDate < moment(accountingPeriod?.start).format("YYYY-MM-DD") &&
            //     currentAlAcrData.amountSigned !== currentAlAcrData.depreciableAmountSigned) {
            //     const gap = mathFix(currentAlAcrData.amountSigned - currentAlAcrData.depreciableAmountSigned, 2)

            //     const date = getDBFormattedDate(accountingPeriod?.start)
            //     aLObject6811.date = date
            //     aLObject6811.amountSigned = gap
            //     aLObject6811.amount = Math.abs(gap)
            //     linear68AccountingLines.push({ ...aLObject6811 });

            //     aLObject28.date = date
            //     aLObject28.amountSigned = -gap
            //     aLObject28.amount = Math.abs(gap)
            //     linear28AccountingLines.push({ ...aLObject28 })
            // }
        }

        const linearAccountingLines = [...linear68AccountingLines, ...linear28AccountingLines].sort(
            (a, b) => {
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            },
        );
        // ++ end of linearAccountingLines collect

        if (currentAlAcrData.linearDepreciation) {
            currentAlAcrData.decliningSpreadStartDate = undefined;
            currentAlAcrData.decliningSpreadEndDate = undefined;
        } else {
            // ++++++++++++++++++++++++++++++++++++++++++++++
            // ++++++++ DECLINING BALANCE DEPRECATION *********
            let decliningFirstPeriodMonthsN = 0;
            // let firstPeriodMonthsLength = 0
            let derogationDate = "";

            currentAlAcrData.decliningSpreadStartDate = moment(
                currentAlAcrData.linearSpreadStartDate,
            )
                .clone()
                .startOf("month")
                .startOf("day")
                .format("YYYY-MM-DD");

            // /////// TODO : if DECLINING
            // /////// ACCOUNTINGPERIODS ?
            // /////// NEEDS OF REALS FIRST ACCOUNTINGPERIOD START + END

            const linearStartAccountingPeriod = currentAlAcrData.alFirstPeriod;

            const decliningStartAccountingPeriod = currentAlAcrData.alFirstPeriod;

            // ++ no accountingPeriods
            if (!decliningStartAccountingPeriod || !linearStartAccountingPeriod) {
                console.error(
                    "CheckFixedAssetsToDepreciate: no decliningStartAccountingPeriod or linearStartAccountingPeriod",
                );
            } else if (
                linearStartAccountingPeriod &&
                linearStartAccountingPeriod.end &&
                decliningStartAccountingPeriod &&
                decliningStartAccountingPeriod.end
            ) {
                decliningFirstPeriodMonthsN =
                    moment(decliningStartAccountingPeriod.end).diff(
                        moment(currentAlAcrData.decliningSpreadStartDate),
                        "months",
                    ) + 1;

                currentAlAcrData.decliningSpreadMonthCount =
                    decliningFirstPeriodMonthsN + (currentAlAcrData.selectedYearDuration - 1) * 12;

                currentAlAcrData.decliningSpreadEndDate = moment(decliningStartAccountingPeriod.end)
                    .clone()
                    .add(currentAlAcrData.selectedYearDuration - 1, "years")
                    .startOf("day")
                    .format();

                if (!currentAlAcrData.rateDeclining) {
                    const factorDeclining = decliningBalanceFactor.find(
                        (f) =>
                            f.minYearDuration <= currentAlAcrData.selectedYearDuration &&
                            (f.maxYearDuration >= currentAlAcrData.selectedYearDuration ||
                                f.maxYearDuration === null),
                    );

                    currentAlAcrData.rateDeclining =
                        (100 / currentAlAcrData.selectedYearDuration) * factorDeclining.factor;
                }

                // // ?? TODO ?? to check if necessary
                // currentAlAcrData.nbDaysFirstYearPeriod = null
                // currentAlAcrData.nbDaysLastYearPeriod = null
                // //

                subDate = moment(currentAlAcrData.decliningSpreadStartDate).format();

                let decliningNewDate = getDBFormattedEndMonthDate(subDate);

                let selectedNetAmount = 0;

                // ++++++++++++ DECLINING first year ++++++++++++
                let decliningNetBookValue = currentAlAcrData.netAmountOriginalValue;

                const firstYearNetAmountSigned = mathFix(
                    (((parseInt(depreciationBasis, 10) * currentAlAcrData.rateDeclining) / 100) *
                        decliningFirstPeriodMonthsN) /
                        12,
                    2,
                );

                const firstYearMonthAmountSigned = mathFix(
                    firstYearNetAmountSigned / decliningFirstPeriodMonthsN,
                    2,
                );

                for (let month = 0; month < decliningFirstPeriodMonthsN; month++) {
                    if (month > 0) {
                        decliningNewDate = getDBFormattedEndMonthDate(
                            moment(decliningNewDate).add(1, "days"),
                        );
                    }

                    const alDeclinining = {
                        date: decliningNewDate,
                        amountSigned: mathFix(firstYearMonthAmountSigned, 2),
                    };

                    // ** decliningAccountingLines contains only one line per month
                    // ** D/C are calculated in derogation6878AccountingLines + derogation145AccountingLines
                    // ** containing only gap between linear and declining
                    decliningAccountingLines.push(alDeclinining);
                }

                const decliningDepreciationTable = [
                    {
                        date: decliningNewDate, // ** date 30-11-2019
                        decliningYearAmountSigned: firstYearNetAmountSigned, // ** full year declining 58.33
                        rateDeclining: currentAlAcrData.rateDeclining, // ** 41.67%
                        decliningCurrentMonthAmountSigned: firstYearMonthAmountSigned, // ** month declining 29.16 ??
                        decliningNetBookValue: mathFix(
                            decliningNetBookValue + firstYearMonthAmountSigned,
                            2,
                        ), // ** VNC 941,67
                        decliningPeriodMonthsN: decliningFirstPeriodMonthsN, // ** 2 months
                    },
                ];

                const firstYearDecliningSum = decliningAccountingLines?.length
                    ? mathFix(
                          decliningAccountingLines.reduce((a, b) => a + b.amountSigned, 0),
                          2,
                      )
                    : 0;

                decliningNetBookValue = mathFix(decliningNetBookValue - firstYearDecliningSum, 2);

                // ++++++++++++ DECLINING others years ++++++++++++
                // ** remaining to depreciate

                for (let yNb = 1; yNb < currentAlAcrData.selectedYearDuration; yNb++) {
                    if (currentAlAcrData.selectedYearDuration === 2) {
                        selectedNetAmount = decliningNetBookValue;
                    } else {
                        // ** tests if linear depreciation is > declining
                        const otherYearDecliningNetAmount = mathFix(
                            (decliningNetBookValue * currentAlAcrData.rateDeclining) / 100,
                            2,
                        );
                        const otherYearsLinearNetAmount = mathFix(
                            decliningNetBookValue / (currentAlAcrData.selectedYearDuration - yNb),
                            2,
                        );

                        selectedNetAmount = otherYearDecliningNetAmount;

                        if (otherYearDecliningNetAmount < otherYearsLinearNetAmount) {
                            selectedNetAmount = otherYearsLinearNetAmount;
                        }
                    }

                    // ++++ DECLINING months in year +++++++++++++++++++
                    let monthAmountSigned = mathFix(selectedNetAmount / 12, 2);

                    for (let m = 0; m < 12; m++) {
                        decliningNewDate = getDBFormattedEndMonthDate(
                            moment(decliningNewDate).add(1, "days"),
                        );

                        // ** last depreciation: monthAmount === remaining to depreciate
                        if (
                            decliningNewDate.startsWith(
                                moment(currentAlAcrData.decliningSpreadEndDate).format(
                                    "YYYY-MM-DD",
                                ),
                            )
                        ) {
                            let previousDepreciations = mathFix(
                                decliningAccountingLines.reduce((a, b) => a + b.amountSigned, 0),
                                2,
                            );

                            monthAmountSigned = mathFix(
                                currentAlAcrData.netAmountOriginalValue - previousDepreciations,
                                2,
                            );
                        }

                        // const linearAlSameDate = linearAccountingLines.find(lal => moment(moment(lal.date).format("YYYY-MM")).isSame(moment(moment(decliningNewDate).format("YYYY-MM"))))

                        decliningAccountingLines.push({
                            date: decliningNewDate,
                            amountSigned: mathFix(monthAmountSigned, 2),
                        });
                        decliningNetBookValue = mathFix(
                            decliningNetBookValue - monthAmountSigned,
                            2,
                        );
                    }
                }
                // ++++ end of decliningAccountingLines collect

                // ++++++++++++++++++++++++++++++++++++++++++++++
                // ++++++++ DEROGATION BALANCE DEPRECATION *********

                // ++ first year derogation
                let firstYearDerogation = 0;
                let firstDerogationAccountingPeriod = decliningStartAccountingPeriod || {};

                if (
                    moment(linearStartAccountingPeriod.end).format("YYYY-MM-DD") <=
                    moment(decliningStartAccountingPeriod.end).format("YYYY-MM-DD")
                ) {
                    // ++ same accountingPeriod
                    // ++ start L + D 01-11-2019
                    // |      |    L   |   decl  |   derog  |
                    // | 2019 |  33.33 |   58.33 |    25.00 |
                    // | 2020 | 200.00 |  329.58 |   129.58 |
                    // | 2021 | 200.00 |  214.23 |    14.23 |
                    // | 2022 | 200.00 |  198.93 |    -1.07 |
                    // | 2023 | 200.00 |  198.93 |    -1.07 |
                    // | 2024 | 166.67 |         |  -166.67 |
                    // |------------------------------------|
                    // ++ linear before declining
                    // |      |    L   |   decl  |   derog  |
                    // | 2019 |  33.33 |         |          |
                    // | 2020 | 200.00 |   58.33 |  -175.00 |
                    // | 2021 | 200.00 |  329.58 |   129.58 |
                    // | 2022 | 200.00 |  214.23 |    14.23 |
                    // | 2023 | 200.00 |  198.93 |    -1.07 |
                    // | 2024 | 166.67 |  198.93 |    32.26 |
                    // |------------------------------------|

                    let linearTillEndOfFirstDecliningYear = [];

                    linearTillEndOfFirstDecliningYear = linearAccountingLines.filter(
                        (lal) =>
                            moment(lal.date).format("YYYY-MM") <=
                            moment(decliningStartAccountingPeriod.end).format("YYYY-MM"),
                    );

                    const linearTillEndOfFirstDecliningYear68 =
                        linearTillEndOfFirstDecliningYear.filter((pal) =>
                            pal.accountableAccount?.connect?.uniqueName?.name?.startsWith("68"),
                        );

                    const previousLinearDepreciationsSum =
                        linearTillEndOfFirstDecliningYear68 &&
                        linearTillEndOfFirstDecliningYear68.length
                            ? mathFix(
                                  linearTillEndOfFirstDecliningYear68.reduce(
                                      (a, b) => a + b.amountSigned,
                                      0,
                                  ),
                                  2,
                              )
                            : 0;

                    firstYearDerogation = mathFix(
                        firstYearDecliningSum - previousLinearDepreciationsSum,
                        2,
                    );
                } else {
                    // ++ linear after declining
                    // |      |    L   |   decl  |   derog  |
                    // | 2019 |        |   58.33 |    58.33 |
                    // | 2020 |  33.33 |  329.58 |   296.25 |
                    // | 2021 | 200.00 |  214.23 |    14.23 |
                    // | 2022 | 200.00 |  198.93 |    -1.07 |
                    // | 2023 | 200.00 |  198.93 |    -1.07 |
                    // | 2024 | 200.00 |         |  -200.00 |
                    // | 2025 | 166.67 |         |  -166.67 |
                    // |------------------------------------|
                    firstYearDerogation = mathFix(firstYearDecliningSum, 2);
                }

                const firstYearMonthDerogation = mathFix(
                    firstYearDerogation / decliningFirstPeriodMonthsN,
                    2,
                );

                let gapFirstMonthRegulDerogationMonthAmountSigned = firstYearMonthDerogation;

                const derogationWording =
                    firstYearMonthDerogation > 0
                        ? "Dot. amos dérog. " + currentAlAcrData.fixedAssetWording
                        : "Repr. Amo Dérog " + currentAlAcrData.fixedAssetWording;

                derogationDate = getDBFormattedEndMonthDate(
                    moment(currentAlAcrData.decliningSpreadStartDate),
                );

                for (let nn = 0; nn < decliningFirstPeriodMonthsN; nn++) {
                    if (nn > 0) {
                        const previousMonth = moment(derogationDate).month();

                        derogationDate = getDBFormattedEndMonthDate(
                            moment(derogationDate).add(1, "days"),
                        );
                    }

                    // ++ same accountingPeriod
                    // ++ start L 25-11-2019 + D 01-11-2019
                    // |      |    L   |   decl  |   derog  |
                    // | 2019 |  33.33 |   58.33 |    25.00 |
                    // | 2020 | 200.00 |  329.58 |   129.58 |
                    // | 2021 | 200.00 |  214.23 |    14.23 |
                    // | 2022 | 200.00 |  198.93 |    -1.07 |
                    // | 2023 | 200.00 |  198.93 |    -1.07 |
                    // | 2024 | 166.67 |         |  -166.67 |

                    if (moment(derogationDate).format("YYYY-MM-DD") == lastClosedPeriodEnd) {
                        const derogationDepreciationTable = [
                            {
                                date: derogationDate, // ** date 30-11-2019
                                yearDerogationAmountSigned: firstYearDerogation, // ** full year declining 58.33
                                rateDeclining: currentAlAcrData.rateDeclining, // ** 41.67%
                                derogationCurrentMonthAmountSigned: firstYearMonthDerogation, // ** month declining 29.16 ??
                                // decliningNetBookValue: mathFix(decliningNetBookValue + firstYearMonthAmountSigned, 2), // ** VNC 941,67
                                decliningPeriodMonthsN: decliningFirstPeriodMonthsN, // ** 2 months
                            },
                        ];

                        // eslint-disable-next-line no-await-in-loop
                        const testErrors: any = await errorOnRegul68DerogationGap(
                            currentAlAcrData,
                            derogationDepreciationTable,
                            derogation6878AccountingLines,
                        );

                        if (testErrors) {
                            if (testErrors?.errorOnRegul6878DerogGap?.suggestedDate) {
                                return testErrors;
                            } else {
                                gapFirstMonthRegulDerogationMonthAmountSigned = testErrors;
                            }
                        }
                    }

                    derogation6878AccountingLines.push({
                        date: derogationDate,
                        wording: derogationWording,
                        amountSigned: firstYearMonthDerogation,
                        amount: Math.abs(firstYearMonthDerogation),
                        // eslint-disable-next-line no-await-in-loop
                        accountableAccount:
                            firstYearMonthDerogation > 0
                                ? await getAuditConnectAccountableAccount(store, apollo, "687250")
                                : await getAuditConnectAccountableAccount(store, apollo, "787250"),
                        type: "MANAGACCOU",
                        company: { connect: { id: company.id } },
                    });

                    derogation145AccountingLines.push({
                        date: derogationDate,
                        wording: derogationWording,
                        amountSigned: -firstYearMonthDerogation,
                        amount: Math.abs(firstYearMonthDerogation),
                        // eslint-disable-next-line no-await-in-loop
                        accountableAccount: await getAuditConnectAccountableAccount(
                            store,
                            apollo,
                            "145000",
                            undefined,
                            undefined,
                            true,
                        ),
                        type: "MANAGACCOU",
                        company: { connect: { id: company.id } },
                    });
                }

                // ++ end of first derogation year

                // ++ others derogation years

                for (let yDero = 0; yDero < currentAlAcrData.selectedYearDuration; yDero++) {
                    const startCurrentPeriod = moment(derogationDate)
                        .add(1, "days")
                        .startOf("day")
                        .format();
                    const endCurrentPeriod = moment(startCurrentPeriod)
                        .add(1, "year")
                        .add(-1, "days")
                        .endOf("day")
                        .format();

                    // ** reduce current year
                    // ** linear
                    const currentYearLinearAL = linearAccountingLines.filter(
                        (lal) =>
                            moment(moment(lal.date).format("YYYY-MM-DD")).isSameOrAfter(
                                moment(startCurrentPeriod).format("YYYY-MM-DD"),
                            ) &&
                            moment(moment(lal.date).format("YYYY-MM-DD")).isSameOrBefore(
                                moment(endCurrentPeriod).format("YYYY-MM-DD"),
                            ),
                    );

                    const currentYearLinearAL68 =
                        currentYearLinearAL && currentYearLinearAL.length
                            ? currentYearLinearAL.filter((cyal) =>
                                  cyal.accountableAccount?.connect?.uniqueName?.name?.startsWith(
                                      "68",
                                  ),
                              )
                            : undefined;

                    const currentYearLinearAlSum = currentYearLinearAL68
                        ? mathFix(
                              sumBy(currentYearLinearAL68, function (cyal68) {
                                  return cyal68.amountSigned;
                              }),
                              2,
                          )
                        : 0;

                    // ** reduce current year
                    // ** declining
                    const startCurrentPeriodYYYYMMDD =
                        moment(startCurrentPeriod).format("YYYY-MM-DD");
                    const endCurrentPeriodYYYYMMDD = moment(endCurrentPeriod).format("YYYY-MM-DD");
                    const currentYearDecliningAL = decliningAccountingLines.filter(
                        (lal) =>
                            startCurrentPeriodYYYYMMDD <= moment(lal.date).format("YYYY-MM-DD") &&
                            moment(lal.date).format("YYYY-MM-DD") <= endCurrentPeriodYYYYMMDD,
                    );

                    const currentYearDecliningAlSum =
                        currentYearDecliningAL && currentYearDecliningAL.length
                            ? mathFix(
                                  sumBy(currentYearDecliningAL, function (cyalDecl) {
                                      return cyalDecl.amountSigned;
                                  }),
                                  2,
                              )
                            : 0;

                    const currentYearDerogationSum = mathFix(
                        currentYearDecliningAlSum - currentYearLinearAlSum,
                        2,
                    );

                    let monthDerogationAmountSigned = currentYearDerogationSum
                        ? mathFix(currentYearDerogationSum / 12, 2)
                        : 0;
                    gapFirstMonthRegulDerogationMonthAmountSigned = monthDerogationAmountSigned;

                    if (currentYearDerogationSum && monthDerogationAmountSigned) {
                        for (let mo = 0; mo < 12; mo++) {
                            derogationDate = getDBFormattedEndMonthDate(
                                moment(derogationDate).add(1, "days"),
                            );

                            if (mo === 11) {
                                // ** derogationAmountSigned
                                // ** middles months OR last year && last month
                                monthDerogationAmountSigned = mathFix(
                                    currentYearDerogationSum - monthDerogationAmountSigned * 11,
                                    2,
                                );
                                gapFirstMonthRegulDerogationMonthAmountSigned =
                                    monthDerogationAmountSigned;
                            }

                            if (
                                moment(derogationDate).format("YYYY-MM-DD") == lastClosedPeriodEnd
                            ) {
                                const derogationDepreciationTable = [
                                    {
                                        date: derogationDate, // ** date 30-11-2019
                                        yearDerogationAmountSigned: currentYearDerogationSum, // ** full year declining 58.33
                                        rateDeclining: currentAlAcrData.rateDeclining, // ** 41.67%
                                        derogationCurrentMonthAmountSigned:
                                            monthDerogationAmountSigned, // ** month declining 29.16 ??
                                        // decliningNetBookValue: mathFix(decliningNetBookValue + firstYearMonthAmountSigned, 2), // ** VNC 941,67
                                        decliningPeriodMonthsN: 12, // ** 12 months
                                    },
                                ];

                                // eslint-disable-next-line no-await-in-loop
                                const testErrors: any = await errorOnRegul68DerogationGap(
                                    currentAlAcrData,
                                    derogationDepreciationTable,
                                    derogation6878AccountingLines,
                                );

                                if (testErrors) {
                                    if (testErrors?.errorOnRegul6878DerogGap?.suggestedDate) {
                                        return testErrors;
                                    } else {
                                        gapFirstMonthRegulDerogationMonthAmountSigned = testErrors;
                                        console.error("🚀 - testErrors:", testErrors);
                                        console.error(
                                            "🚀 - gapFirstMonthRegulDerogationMonthAmountSigned:",
                                            gapFirstMonthRegulDerogationMonthAmountSigned,
                                        );
                                        debugger;
                                    }
                                }
                            }

                            const derogationWording =
                                monthDerogationAmountSigned > 0
                                    ? "Dot. amos dérog. " + currentAlAcrData.fixedAssetWording
                                    : "Repr. Amo Dérog " + currentAlAcrData.fixedAssetWording;

                            derogation6878AccountingLines.push({
                                date: derogationDate,
                                wording: derogationWording,
                                amountSigned: mathFix(
                                    gapFirstMonthRegulDerogationMonthAmountSigned,
                                    2,
                                ),
                                amount: Math.abs(
                                    mathFix(gapFirstMonthRegulDerogationMonthAmountSigned, 2),
                                ),
                                // eslint-disable-next-line no-await-in-loop
                                accountableAccount:
                                    monthDerogationAmountSigned > 0
                                        ? await getAuditConnectAccountableAccount(
                                              store,
                                              apollo,
                                              "687250",
                                          )
                                        : await getAuditConnectAccountableAccount(
                                              store,
                                              apollo,
                                              "787250",
                                          ),
                                type: "MANAGACCOU",
                                company: { connect: { id: company.id } },
                            });

                            derogation145AccountingLines.push({
                                date: derogationDate,
                                wording: derogationWording,
                                amountSigned: -mathFix(
                                    gapFirstMonthRegulDerogationMonthAmountSigned,
                                    2,
                                ),
                                amount: Math.abs(
                                    mathFix(gapFirstMonthRegulDerogationMonthAmountSigned, 2),
                                ),
                                // eslint-disable-next-line no-await-in-loop
                                accountableAccount: await getAuditConnectAccountableAccount(
                                    store,
                                    apollo,
                                    "145000",
                                ),
                                type: "MANAGACCOU",
                                company: { connect: { id: company.id } },
                            });
                        }
                    }
                }

                derogationAccountingLines = [
                    ...derogation6878AccountingLines,
                    ...derogation145AccountingLines,
                ].sort((a, b) => {
                    if (a.date < b.date) return -1;
                    if (a.date > b.date) return 1;
                    return 0;
                });
                // ** end of collect of derogationAccountingLines
                // ** end of collect of accountingLines *****
            }
        }

        // +++++++++++++ GENERATE DEPRECIATIONS ++++++++++++
        // +++++++++++++  GENERATE TABLE *****************
        currentAlAcrData.depreciationTable = await generateDepreciationTable(
            currentAlAcrData,
            linearAccountingLines,
            decliningAccountingLines,
            derogationAccountingLines,
            depreciationBasis,
            accountingPeriod,
        );

        // ++++++ GENERATE DEPRECIATIONS +++++++
        // ++ DOCUMENTS +++++++++++++++++++++
        // ** create monthly documents in MANAGACCOU
        const accountingLines = [];
        if (derogationAccountingLines.length) {
            accountingLines.push(...linearAccountingLines, ...derogationAccountingLines);

            accountingLines.sort((a, b) => {
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            });
        } else {
            accountingLines.push(...linearAccountingLines);
        }

        if (accountingLines?.length) {
            let alsInOpenPeriods = [];

            if (!lastClosedPeriodEnd) {
                alsInOpenPeriods = accountingLines;
            } else {
                alsInOpenPeriods = accountingLines.filter(
                    (al) => moment(al.date).format("YYYY-MM-DD") > lastClosedPeriodEnd,
                );
            }

            let documents = [];

            if (alsInOpenPeriods?.length) {
                documents = await generateDocumentsFromAccountingLines(
                    currentAlAcrData.fixedAssetWording,
                    alsInOpenPeriods,
                    company.id,
                    currentUserId,
                    apollo,
                );
            }

            for (const al of accountingLines) {
                computeDebitCredit(al);
            }
            // ** store empty or full documents array
            // currentAlAcrData.alDocuments = this.documents
            currentAlAcrData.accountingLines = accountingLines;

            return {
                currentAlAcrData: currentAlAcrData,
                documents: documents,
            };
        }
    }
}

export async function generateDocumentsFromAccountingLines(
    alWording,
    accountingLines,
    companyId,
    currentUserId,
    apollo,
) {
    const documents = [];
    let documentAls = [];
    for (let x = 0; x < accountingLines.length; x++) {
        // ** only if period is not closed
        documentAls.push(accountingLines[x]);

        let sameYYYYMM = false;
        let date = accountingLines[x].date;

        if (accountingLines[x + 1]) {
            sameYYYYMM =
                moment(date).format("YYYY-MM") ==
                moment(accountingLines[x + 1].date).format("YYYY-MM");
        }

        if (!sameYYYYMM || !accountingLines[x + 1]) {
            const documentALsum = sumBy(documentAls, "amountSigned");

            if (!documentALsum) {
                let wording = "Dot. Amos - " + alWording;

                wording += " - " + formatDate(date);

                // eslint-disable-next-line no-await-in-loop
                const afterSituationDate = await getAccountingClosingDate(apollo, companyId, date);
                if (afterSituationDate) {
                    date = afterSituationDate;
                    for (const al of documentAls) {
                        al.date = afterSituationDate;
                        al.wording = wording;
                    }
                }

                const doc = getEmptyDocument(
                    companyId,
                    wording,
                    "MISCELLANEOUS_OPERATION",
                    date,
                    currentUserId,
                    null,
                    documentAls,
                    true, // accountingLinesLocked
                );

                documents.push(doc);

                documentAls = [];
            } else {
                console.error("GROS BUG : écriture pas équilibrée");
            }
        }
    }
    return documents;
}

export async function generateDepreciationTable(
    currentAlAcrData,
    linearAccountingLines,
    decliningAccountingLines,
    derogationAccountingLines,
    depreciationBasis,
    accountingPeriod,
) {
    const resume = [];
    const resumeAccountingLines = [];

    if (
        currentAlAcrData.linearDepreciation === false &&
        decliningAccountingLines?.length &&
        derogationAccountingLines?.length
    ) {
        resumeAccountingLines.push(
            ...linearAccountingLines,
            ...decliningAccountingLines,
            ...derogationAccountingLines,
        );

        resumeAccountingLines.sort((a, b) => {
            if (a.date < b.date) return -1;
            if (a.date > b.date) return 1;
            return 0;
        });
    } else {
        resumeAccountingLines.push(...linearAccountingLines);
    }

    let resumePeriodEnd = currentAlAcrData.alFirstPeriod.end; // ** YYYY-MM-DD

    if (
        moment(resumePeriodEnd).isBefore(moment(resumeAccountingLines[0].date).format("YYYY-MM-DD"))
    ) {
        resumePeriodEnd = moment(resumePeriodEnd).add(1, "years").format("YYYY-MM-DD");
    }

    // ** create resume structure
    resume.push({
        resumePeriodEnd: resumePeriodEnd,
        netBookValueStart: mathFix(depreciationBasis, 2),
        linearYearDepreciation: 0,
        decliningYearDepreciation: 0,
        derogationYearDepreciation: 0,
    });

    for (let y = 0; y < resumeAccountingLines.length; y++) {
        const dateAfterResume = moment(
            moment(resumeAccountingLines[y].date).format("YYYY-MM-DD"),
        ).isAfter(moment(moment(resume[resume.length - 1].resumePeriodEnd).format("YYYY-MM-DD")));

        if (dateAfterResume) {
            resumePeriodEnd = moment(resumePeriodEnd)
                .startOf("day")
                .add(1, "years")
                .format("YYYY-MM-DD");

            resume.push({
                resumePeriodEnd: resumePeriodEnd,
                linearYearDepreciation: 0,
                decliningYearDepreciation: 0,
                derogationYearDepreciation: 0,
            });
        }

        let linearDep = 0;
        let derogDep = 0;

        // ** LINEAR
        if (
            resumeAccountingLines[y].accountableAccount &&
            (resumeAccountingLines[y].accountableAccount?.connect?.uniqueName?.name?.startsWith(
                "6811",
            ) ||
                (resumeAccountingLines[y].accountableAccountName &&
                    resumeAccountingLines[y].accountableAccountName.startsWith("6811")))
        ) {
            linearDep = mathFix(resumeAccountingLines[y].amountSigned, 2);

            if (linearDep) {
                resume[resume.length - 1].linearYearDepreciation += mathFix(linearDep, 2);
            }
        } else if (!resumeAccountingLines[y].wording) {
            // ** DECLINING
            resume[resume.length - 1].decliningYearDepreciation +=
                resumeAccountingLines[y].amountSigned;
        } else if (
            // ** DEROGATION
            resumeAccountingLines[y].accountableAccount &&
            ((resumeAccountingLines[y].accountableAccount.connect &&
                resumeAccountingLines[y].accountableAccount.connect.uniqueName?.name &&
                (resumeAccountingLines[y].accountableAccount.connect.uniqueName.name.startsWith(
                    "68725",
                ) ||
                    resumeAccountingLines[y].accountableAccount.connect.uniqueName.name.startsWith(
                        "78725",
                    ))) ||
                (resumeAccountingLines[y].accountableAccount.name &&
                    (resumeAccountingLines[y].accountableAccount.name.startsWith("68725") ||
                        resumeAccountingLines[y].accountableAccount.name.startsWith("78725"))))
        ) {
            derogDep = mathFix(resumeAccountingLines[y].amountSigned, 2);
            if (derogDep) {
                resume[resume.length - 1].derogationYearDepreciation += mathFix(derogDep, 2);
            }
        }
    }

    if (resume.length) {
        for (let y1 = 0; y1 < resume.length; y1++) {
            // ** linear
            resume[y1].linearNetBookValue0101 =
                resume[y1 - 1] && resume[y1 - 1].linearNetBookValue3112
                    ? mathFix(resume[y1 - 1].linearNetBookValue3112, 2)
                    : mathFix(depreciationBasis, 2);

            resume[y1].linearAccumulatedDepreciationStart =
                resume[y1 - 1] && resume[y1 - 1].linearAccumulatedDepreciation
                    ? mathFix(resume[y1 - 1].linearAccumulatedDepreciation, 2)
                    : 0;

            resume[y1].linearYearDepreciation = mathFix(resume[y1].linearYearDepreciation, 2);

            resume[y1].linearAccumulatedDepreciation =
                resume[y1 - 1] &&
                resume[y1 - 1].linearAccumulatedDepreciation &&
                resume[y1] &&
                resume[y1].linearYearDepreciation
                    ? mathFix(
                          resume[y1 - 1].linearAccumulatedDepreciation +
                              resume[y1].linearYearDepreciation,
                          2,
                      )
                    : resume[y1] && resume[y1].linearYearDepreciation
                      ? mathFix(resume[y1].linearYearDepreciation, 2)
                      : 0;

            resume[y1].linearNetBookValue3112 =
                resume[y1 - 1] &&
                resume[y1 - 1].linearNetBookValue3112 &&
                resume[y1] &&
                resume[y1].linearYearDepreciation
                    ? mathFix(
                          resume[y1 - 1].linearNetBookValue3112 - resume[y1].linearYearDepreciation,
                          2,
                      )
                    : resume[y1] && resume[y1].linearYearDepreciation
                      ? mathFix(depreciationBasis - resume[y1].linearYearDepreciation, 2)
                      : 0;

            // ** declining
            resume[y1].decliningNetBookValue0101 =
                resume[y1 - 1] && resume[y1 - 1].decliningNetBookValue3112
                    ? mathFix(resume[y1 - 1].decliningNetBookValue3112, 2)
                    : mathFix(depreciationBasis, 2);

            resume[y1].decliningAccumulatedDepreciationStart =
                resume[y1 - 1] && resume[y1 - 1].decliningAccumulatedDepreciation
                    ? mathFix(resume[y1 - 1].decliningAccumulatedDepreciation, 2)
                    : 0;

            resume[y1].decliningYearDepreciation = resume[y1].decliningYearDepreciation
                ? mathFix(resume[y1].decliningYearDepreciation, 2)
                : 0;

            resume[y1].decliningAccumulatedDepreciation =
                resume[y1 - 1] &&
                resume[y1 - 1].decliningAccumulatedDepreciation &&
                resume[y1].decliningYearDepreciation
                    ? mathFix(
                          resume[y1 - 1].decliningAccumulatedDepreciation +
                              resume[y1].decliningYearDepreciation,
                          2,
                      )
                    : resume[y1].decliningYearDepreciation
                      ? mathFix(resume[y1].decliningYearDepreciation, 2)
                      : 0;

            if (currentAlAcrData.linearDepreciation === false) {
                resume[y1].decliningNetBookValue3112 =
                    resume[y1 - 1] &&
                    resume[y1 - 1].decliningNetBookValue3112 &&
                    resume[y1].decliningYearDepreciation
                        ? mathFix(
                              resume[y1 - 1].decliningNetBookValue3112 -
                                  resume[y1].decliningYearDepreciation,
                              2,
                          )
                        : depreciationBasis && resume[y1].decliningYearDepreciation
                          ? mathFix(depreciationBasis - resume[y1].decliningYearDepreciation, 2)
                          : 0;
            } else {
                resume[y1].decliningNetBookValue3112 = 0;
            }

            // ** derogation
            if (currentAlAcrData.linearDepreciation === false) {
                resume[y1].derogationYearDepreciation =
                    resume[y1] && resume[y1].derogationYearDepreciation
                        ? mathFix(resume[y1].derogationYearDepreciation, 2)
                        : 0;

                resume[y1].derogationAccumulatedDepreciation =
                    resume[y1 - 1] && resume[y1]
                        ? mathFix(
                              resume[y1 - 1].derogationAccumulatedDepreciation +
                                  resume[y1].derogationYearDepreciation,
                              2,
                          )
                        : mathFix(resume[y1].derogationYearDepreciation, 2);

                if (resume[y1].derogationYearDepreciation && !decliningAccountingLines?.length) {
                    resume[y1] = await generateDecliningDepreciationTable(
                        resume,
                        y1,
                        currentAlAcrData,
                    );
                }
            }

            resume[y1].globalNetBookValue3112 =
                resume[y1].decliningNetBookValue3112 || resume[y1].linearNetBookValue3112 || 0;
        }

        // ** if gap between imported alAmountSigned and previousDepreciationAmountSigned, add a line, in this current year
        if (
            currentAlAcrData.linearSpreadEndDate <
                moment(accountingPeriod?.start).format("YYYY-MM-DD") &&
            currentAlAcrData.amountSigned !== currentAlAcrData.previousDepreciationAmountSigned
        ) {
            const gap = mathFix(
                currentAlAcrData.amountSigned - currentAlAcrData.previousDepreciationAmountSigned,
                2,
            );

            const date = moment(accountingPeriod?.end).format("YYYY-MM-DD");
            resume.push({
                resumePeriodEnd: date,
                linearNetBookValue0101: mathFix(
                    resume[resume.length - 1].linearNetBookValue3112,
                    2,
                ),
                linearAccumulatedDepreciationStart: mathFix(
                    resume[resume.length - 1].linearAccumulatedDepreciation,
                    2,
                ),
                linearYearDepreciation: mathFix(gap, 2),
                linearAccumulatedDepreciation: mathFix(
                    resume[resume.length - 1].linearAccumulatedDepreciation + gap,
                    2,
                ),
                linearNetBookValue3112: mathFix(
                    resume[resume.length - 1].linearNetBookValue3112 - gap,
                    2,
                ),
            });
        }

        return resume;
    }
}

export function generateDecliningDepreciationTable(resume, y, currentAlAcrData) {
    // ** declining
    resume[y].decliningNetBookValue0101 =
        resume[y - 1] && resume[y - 1].decliningNetBookValue3112
            ? mathFix(resume[y - 1].decliningNetBookValue3112, 2)
            : mathFix(currentAlAcrData.amountSigned, 2);

    resume[y].decliningAccumulatedDepreciationStart =
        resume[y - 1] && resume[y - 1].decliningAccumulatedDepreciation
            ? mathFix(resume[y - 1].decliningAccumulatedDepreciation, 2)
            : 0;

    resume[y].decliningYearDepreciation =
        resume[y].linearYearDepreciation && resume[y].derogationYearDepreciation
            ? mathFix(resume[y].linearYearDepreciation + resume[y].derogationYearDepreciation, 2)
            : !resume[y].linearYearDepreciation && resume[y].derogationYearDepreciation
              ? resume[y].derogationYearDepreciation
              : resume[y].linearYearDepreciation && !resume[y].derogationYearDepreciation
                ? resume[y].linearYearDepreciation
                : 0;

    resume[y].decliningAccumulatedDepreciation =
        resume[y - 1] &&
        resume[y - 1].decliningAccumulatedDepreciation &&
        resume[y].decliningYearDepreciation
            ? mathFix(
                  resume[y - 1].decliningAccumulatedDepreciation +
                      resume[y].decliningYearDepreciation,
                  2,
              )
            : resume[y].decliningYearDepreciation
              ? mathFix(resume[y].decliningYearDepreciation, 2)
              : 0;

    resume[y].decliningNetBookValue3112 =
        resume[y - 1] &&
        resume[y - 1].decliningNetBookValue3112 &&
        resume[y].decliningYearDepreciation
            ? mathFix(
                  resume[y - 1].decliningNetBookValue3112 - resume[y].decliningYearDepreciation,
                  2,
              )
            : resume[y].decliningYearDepreciation
              ? mathFix(currentAlAcrData.amountSigned - resume[y].decliningYearDepreciation, 2)
              : 0;

    return resume[y];
}

export function minMaxRules(globalVatAmountSigned) {
    return [
        (v) =>
            parseInt(v) <= globalVatAmountSigned ||
            "Valeur maximale " + [globalVatAmountSigned] + "€",
        (v) => parseInt(v) >= 0 || "Valeur minimale >= 0",
    ];
}

export function dataTableHeight(currentAlAcrData) {
    return "min(" + 48 * (currentAlAcrData.depreciationTable.length + 2) + "px, 30vh)";
}

export async function createOrDeleteFixedAssetReconciliations(
    apollo,
    currentAlAcrData,
    acrResults,
    gqlAction,
    companyId,
) {
    let promises = [];
    if (gqlAction === "CREATE_RECONCILIATION") {
        let accountingLines2ToReconciliateIds = [{ id: currentAlAcrData.alId }];
        let accountingLines28ToReconciliateIds = [];
        let accountingLines275ToReconciliateIds = [];

        let reconciliation2Sum = currentAlAcrData.amountSigned;
        let reconciliation28Sum = 0;
        let reconciliation275Sum = 0;
        let reconciliation145Sum = 0;

        for (const item of acrResults) {
            const als2 = item.accountingLines.filter(
                (al) =>
                    !al.accountableAccount?.name?.startsWith("6") &&
                    !al.accountableAccount?.name?.startsWith("7"),
            );
            for (const al of als2) {
                if (al.accountableAccount.name.startsWith("28")) {
                    reconciliation28Sum += al.amountSigned;
                    accountingLines28ToReconciliateIds.push({ id: al.id });
                } else if (al.accountableAccount.name.startsWith("275")) {
                    reconciliation275Sum += al.amountSigned;
                    accountingLines275ToReconciliateIds.push({ id: al.id });
                } else if (al.accountableAccount.name.startsWith("2")) {
                    reconciliation2Sum += al.amountSigned;
                    accountingLines2ToReconciliateIds.push({ id: al.id });
                } else if (al.accountableAccount.name.startsWith("145")) {
                    reconciliation145Sum += al.amountSigned;
                } else {
                    console.error(
                        "🚀 - file: CheckFixedAssetsToDepreciate.vue:3427 - validateFixedAssetOutflow - not starting with '2' accountableAccount.name : ",
                        al.accountableAccount.name,
                    );
                    debugger;
                }
            }
        }

        if (reconciliation28Sum && mathFix(reconciliation28Sum, 2)) {
            if (
                currentAlAcrData.previousDepreciationAmountSigned &&
                currentAlAcrData.previousDepreciationAmountSigned ===
                    mathFix(reconciliation28Sum, 2)
            ) {
                const previousDepreciationSigned = mathFix(
                    -currentAlAcrData.previousDepreciationAmountSigned,
                    2,
                );

                const result2: any = await apollo.query({
                    query: gql`
                        query accountingLines(
                            $companyId: ID!
                            $amountSigned: Float!
                            $wording: String!
                        ) {
                            accountingLines(
                                where: {
                                    company: { id: $companyId }
                                    amountSigned: $amountSigned
                                    wording_contains: $wording
                                    reconciliation: null
                                }
                            ) {
                                id
                                wording
                                amountSigned
                                date
                                accountableAccount {
                                    name
                                }
                            }
                        }
                    `,
                    variables: {
                        companyId: companyId,
                        amountSigned: previousDepreciationSigned,
                        wording: currentAlAcrData.fixedAssetWording,
                    },
                });

                if (result2?.data?.accountingLines) {
                    const previousDepreciationFromImportFecAL =
                        result2?.data?.accountingLines?.find(
                            (al) =>
                                al.accountableAccount?.name?.startsWith("28") &&
                                al.wording.startsWith("Reprise immos + amos"),
                        );
                    if (previousDepreciationFromImportFecAL) {
                        reconciliation28Sum += previousDepreciationFromImportFecAL.amountSigned;
                        accountingLines28ToReconciliateIds.push({
                            id: previousDepreciationFromImportFecAL.id,
                        });
                    }
                }
            }
        }

        if (reconciliation275Sum && mathFix(reconciliation275Sum, 2)) {
            debugger;
            // // TODO
            // // declining depreciation are not accepted in cutPrevious
            // // no attribute like linear previousDepreciationAmountSigned exists for previous declining from import FEC
        }

        if (reconciliation145Sum && mathFix(reconciliation145Sum, 2)) {
            debugger;
            // // TODO
            // // before : reconciliate 145 ?

            // // declining depreciation are not accepted in cutPrevious
            // // no attribute like linear previousDepreciationAmountSigned exists for previous declining from import FEC
        }

        if (
            (!reconciliation28Sum || !mathFix(reconciliation28Sum, 2)) &&
            (!reconciliation275Sum || !mathFix(reconciliation275Sum, 2)) &&
            (!reconciliation2Sum || !mathFix(reconciliation2Sum, 2))
        ) {
            if (accountingLines28ToReconciliateIds?.length) {
                promises.push(
                    apollo.mutate({
                        mutation: CREATE_RECONCILIATION,
                        variables: {
                            type: "DEFAULT",
                            status: "VALIDATED",
                            companyId: companyId,
                            accountingLines: accountingLines28ToReconciliateIds,
                        },
                    }),
                );
            }

            if (accountingLines275ToReconciliateIds?.length) {
                promises.push(
                    apollo.mutate({
                        mutation: CREATE_RECONCILIATION,
                        variables: {
                            type: "DEFAULT",
                            status: "VALIDATED",
                            companyId: companyId,
                            accountingLines: accountingLines275ToReconciliateIds,
                        },
                    }),
                );
            }

            if (accountingLines2ToReconciliateIds?.length) {
                promises.push(
                    apollo.mutate({
                        mutation: CREATE_RECONCILIATION,
                        variables: {
                            type: "DEFAULT",
                            status: "VALIDATED",
                            companyId: companyId,
                            accountingLines: accountingLines2ToReconciliateIds,
                        },
                    }),
                );
            }
        } else {
            console.error(
                " CheckFixedAssetsToDepreciate.vue -  validateFixedAssetOutflow - some reconciliation sum is !== 0: reconciliation2Sum = ",
                reconciliation2Sum,
                ", reconciliation28Sum = ",
                reconciliation28Sum,
                ", reconciliation275Sum: ",
                reconciliation275Sum,
            );
            return {
                error: "AuditServices - createOrDeleteFixedAssetReconciliations - some reconciliation sum is !== 0",
            };
        }
    } else if (gqlAction === "DELETE_RECONCILIATION") {
        const reconciliationIdsToDelete = [];
        for (const item of acrResults) {
            for (const al of item.accountingLines) {
                if (
                    !al.accountableAccount?.name?.startsWith("6") &&
                    !al.accountableAccount?.name?.startsWith("7")
                ) {
                    if (al.reconciliation?.id) {
                        if (!reconciliationIdsToDelete.includes(al.reconciliation.id)) {
                            reconciliationIdsToDelete.push(al.reconciliation.id);
                        }
                    } else {
                        console.error(
                            "🚀 - file: CheckFixedAssetsToDepreciate.vue:3534 - validateFixedAssetOutflow - al.reconciliation.id is null : al.id",
                            al.id,
                        );
                        console.error("al.accountableAccount.name : ", al.accountableAccount.name);
                        debugger;
                    }
                }
            }
        }
        if (reconciliationIdsToDelete?.length) {
            for (const id of reconciliationIdsToDelete) {
                promises.push(
                    apollo.mutate({
                        mutation: DELETE_RECONCILIATION,
                        variables: {
                            reconciliationId: id,
                        },
                    }),
                );
            }
        }
    }

    return { promises: promises };
}

import { createAccountableAccount, padEndAAName } from "../../services/accountableAccountServices";
import { getDefaultAccountableAccount } from "../../../../backend/src/common/accountableAccount/accountableAccountServices";

export async function getAuditConnectAccountableAccount(
    store,
    apollo,
    inName: string,
    padEnd: boolean = true,
    inDescription: string = undefined,
    inLettering: boolean = undefined,
) {
    if (inName) {
        if (store?.state?.accountableAccountTable?.id) {
            let name = padEndAAName(inName, store, padEnd);

            let accountableAccount = getAuditAccountableAccount(store, name);
            if (!accountableAccount) {
                const defaultAccountableAccount = await getDefaultAccountableAccount(inName);
                if (inLettering) {
                    defaultAccountableAccount.lettering = inLettering;
                }
                const accountableAccountData = cloneDeep(defaultAccountableAccount);
                accountableAccountData.name = name;

                if (inDescription) {
                    accountableAccountData.description = inDescription;
                }

                accountableAccount = await createAccountableAccount(
                    apollo,
                    store.state.accountableAccountTable.id,
                    accountableAccountData,
                );
                if (accountableAccount) {
                    store.state.accountableAccountTable.accountableAccounts.push(
                        accountableAccount,
                    );
                } else {
                    console.error("createAccountableAccount failed");
                    debugger;
                }
            }
            if (accountableAccount?.id) {
                // return {connect: {id:accountableAccount.id}}
                return {
                    connect: {
                        uniqueName: {
                            name: accountableAccount.name,
                            accountableAccountTableId: store.state.accountableAccountTable.id,
                        },
                    },
                };
            }
        } else {
            console.error("accountableAccountTable undefined");
            debugger;
        }
    } else {
        console.error("inName null");
        debugger;
    }
    return undefined;
}

export function getAuditAccountableAccount(store, aaName) {
    const accountableAccount = store.state.accountableAccountTable.accountableAccounts.find(
        (aa) => aa.name.replace(/0+$/, "") === aaName.replace(/0+$/, ""),
    );
    return accountableAccount;
}

export function formatConnectedDocuments(connectedDocuments: Array<any>) {
    let connectedDocumentsIds = [];
    if (connectedDocuments?.length) {
        if (typeof connectedDocuments[0] === "string") {
            connectedDocumentsIds = connectedDocuments.map((id) => ({ id: id }));
        } else if (connectedDocuments[0]?.id) {
            connectedDocumentsIds = connectedDocuments.map((doc) => ({ id: doc.id }));
        } else {
            debugger
            console.error("connectedDocuments is not an array of objects or strings", connectedDocuments);
        }
    } 
    return connectedDocumentsIds;
}
